import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  AppDialogSection,
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
} from '@/UI/AppDialog/AppDialog';
import { Fade } from '@/UI/Transitions/Fade';
import { TextSmMd, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { styled } from '@/UI/theme.exports';
import { useWithLoader } from '@/hooks/useWithLoader';
import { TourStepMediaType } from '@/enums/tour-step-media-type.enum';
import { color as colors } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FlexCenHorVer, FlexColCenVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useTour } from '../TourContext';
import type { TourAction, TourStepMedia } from '../tourTypes';

interface TourActionButtonProps {
  action: TourAction;
  onClick: () => void;
  isLoading: boolean;
}

const SquareButton = styled('button')({
  backgroundColor: 'transparent',
  border: '1px solid rgba(240, 240, 229, 0.2)',
  cursor: 'pointer',
  width: '120px',
  height: '120px',
  padding: '24px 36px',
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '8px',
  '&:hover': {
    backgroundColor: 'rgba(240, 240, 229, 0.1)',
  },
});

export const TourActionButton = ({ action, onClick, isLoading }: TourActionButtonProps) => {
  const { t } = useTranslation();

  const media = useMemo(() => {
    const defaultMedia = action.media?.default;
    if (defaultMedia?.type === TourStepMediaType.IMAGE || defaultMedia?.type === TourStepMediaType.SVG) {
      return defaultMedia.path;
    }

    return null;
  }, [action.media]);

  if (action.type === 'button') {
    let mode: 'filled-light' | 'text' = 'filled-light';
    if (action.variant === 'secondary') {
      mode = 'text';
    }

    return (
      <ButtonContained size="small" loading={isLoading} mode={mode} onClick={onClick}>
        {t(action.label)}
      </ButtonContained>
    );
  }

  return (
    <SquareButton disabled={isLoading} onClick={onClick}>
      {media ? <img src={media} alt={action.label} /> : null}
      <TextSmMd>{t(action.label)}</TextSmMd>
    </SquareButton>
  );
};

interface TourModalProps {
  actions?: TourAction[];
  disallowClose?: boolean;
  content: string;
  media?: TourStepMedia;
  onClose: () => void;
  open: boolean;
  title: string;
}

export const TourModal = ({
  open,
  onClose,
  media: _,
  title,
  content,
  actions,
  disallowClose = false,
}: TourModalProps) => {
  const { callAction } = useTour();
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const { isLoading, execute: runAction } = useWithLoader(callAction, { sync: true });

  return (
    <AppDialog
      open={open}
      onClose={disallowClose ? undefined : onClose}
      width={isFplMigrationEnabled ? 'md' : 340}
      slots={{
        transition: Fade,
      }}
      slotProps={{
        paper: {
          sx: {
            background: colors.Black92,
          },
        },
        transition: {
          timeout: 0,
        },
        backdrop: {
          slotProps: {
            transition: {
              timeout: 0,
            },
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ pt: 5.25, px: 5.25, pb: 0 }}>
          <AppDialogTitle>{t(title)}</AppDialogTitle>
        </AppDialogHeader>
        <AppDialogContent
          sx={{
            px: 5.25,
            pb: 5.25,
            mt: 1.5,
          }}
        >
          <FlexColCenVer
            sx={{
              gap: 2,
            }}
          >
            <TextStdRg
              color="inherit"
              sx={{
                whiteSpace: 'pre-line',
              }}
            >
              {t(content)}
            </TextStdRg>
            <FlexCenHorVer sx={{ gap: 1.5 }}>
              {actions?.map((action) => (
                <TourActionButton
                  key={action.label}
                  action={action}
                  onClick={() => runAction(action)}
                  isLoading={isLoading}
                />
              ))}
            </FlexCenHorVer>
          </FlexColCenVer>
        </AppDialogContent>
      </AppDialogSection>
    </AppDialog>
  );
};
